import { PageChangedEvent } from '@iris/common/modules/grid/ag-grid/extensions/pagination/page-changed-event';
import { AgGridAngular } from 'ag-grid-angular';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';
import { IrisGridMode } from '@iris/common/modules/grid/ag-grid/models/iris-grid-mode';
import isUndefined from 'lodash/isUndefined';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';

const FIRST_PAGE = 1;
const MIN_PAGES_COUNT = 1;
const PAGE_SIZES = [10, 25, 50];

@Component({
  selector: 'iris-ag-grid-paging-panel',
  templateUrl: './paging-panel.component.html',
  styleUrls: ['./paging-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AgGridPagingPanelComponent implements OnChanges {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalSandbox = inject(IrisGlobalSandbox);

  @Input() agGrid: AgGridAngular;
  @Input('irisGridMode') gridMode: IrisGridMode;
  @Input('irisShowPageSelector') showPageSelector: boolean;
  @Input('irisPageSelectorWidth') pageSelectorWidth: number;
  @Input('irisShowPageSizeSelector') showPageSizeSelector: boolean;
  @Input('irisShowNumberOfCurrentItems') showNumberOfCurrentItems: boolean;
  @Input('irisPage') page: number = FIRST_PAGE;
  @Input('irisTotalItemsCount') totalItemsCount: number;
  @Input('irisPageSizes') pageSizes: number[];
  @Input('irisPageSize') pageSize: number;
  @Input('irisSelectedRowsCount') selectedRowsCount: number;
  @Input('irisLastPage') lastPage = false;

  @Output() irisPageChanged = new EventEmitter<PageChangedEvent>();
  @Output() irisPageSizeChanged = new EventEmitter<number>();
  pagesCount = MIN_PAGES_COUNT;

  readonly firstPage = FIRST_PAGE;
  readonly toolbarFiltersStylingMode = this.globalSandbox.toolbarFiltersStylingMode;

  get isFirstPage(): boolean {
    return this.page == FIRST_PAGE;
  }

  get isLastPage(): boolean {
    if (this.totalItemsCount == null) { return this.lastPage; }
    return this.page == this.pagesCount;
  }

  get currentItems(): number {
    return this.isLastPage ? this.totalItemsCount : this.page * this.pageSize;
  }

  ngOnChanges(changes: SimpleChanges<this>): void {
    this._fixWrongNumericProps();
    const changedProps = Object.keys(changes) as (keyof AgGridPagingPanelComponent)[];
    this._extraActionsOnChanges(changedProps);
  }

  /**
   * This method should be called after properties changes made dynamically.
   * @param resetPage - true if the page should be resetted.
   */
  refresh(changes: (keyof AgGridPagingPanelComponent)[]): void {
    this._fixWrongNumericProps();
    this._extraActionsOnChanges(changes);
    this.changeDetectorRef.markForCheck();
  }

  onPageSizeChanged(): void {
    this._calculatePagesCount();
    this.setPage(FIRST_PAGE);
    this.irisPageSizeChanged.emit(this.pageSize);
  }

  setPage(page: number): void {
    if ((page > this.pagesCount || page < 1) && isUndefined(this.lastPage)) {
      this.page = 1;
    } else {
      this.page = page;
    }
    this._notifyPageChanged();
  }

  _extraActionsOnChanges(changes: (keyof AgGridPagingPanelComponent)[]): void {
    if (changes.includes('pageSize') && !changes.includes('page')) {
      this.setPage(FIRST_PAGE);
    }

    if (changes.includes('pageSize') || changes.includes('totalItemsCount')) {
      this._calculatePagesCount();
    }

    if (changes.includes('page') && this.gridMode === 'CLIENT') {
      this.agGrid?.api.paginationGoToPage(this.page - 1);
    }

    if (changes.includes('pageSize') && this.gridMode === 'CLIENT') {
      this.agGrid?.api.setGridOption('paginationPageSize', this.pageSize);
    }
  }

  _notifyPageChanged(): void {
    this.irisPageChanged?.emit({
      page: this.page,
      pageSize: this.pageSize,
    });

    if (this.gridMode === 'CLIENT') {
      this.agGrid?.api.setGridOption('paginationPageSize', this.pageSize);
      this.agGrid?.api.paginationGoToPage(this.page - 1);
    }
  }

  _calculatePagesCount(): void {
    this.pagesCount = Math.ceil((this.totalItemsCount ?? NaN) / (this.pageSize || NaN)) || MIN_PAGES_COUNT;
  }

  _fixWrongNumericProps(): void {
    this.pageSizes ??= PAGE_SIZES;
    this.pageSize ??= this.pageSizes.length ? this.pageSizes[0] : PAGE_SIZES[0];
  }
}
